import type { SiteManifest } from './types';

/**
 * SocialPreneur "Asta Cafe" site-content manifest. Defaults are the handoff
 * copy VERBATIM from `apps/member/src/tenants/socialpreneur/storefront/` — an
 * un-edited site renders byte-identical.
 *
 * Image defaults are literal `/tenants/socialpreneur/...` public paths (the
 * committed assets). Manifests are plain data, so the optional build-time
 * `VITE_MEDIA_URL` base from tokens.ts does not apply to defaults — it was
 * never used in production. Uploaded replacements are R2 keys.
 *
 * Code-owned by design: lucide icons + accent colors (rows pair by index —
 * `fixedLength` where the pairing must hold), NAV anchors, footer columns.
 */
export const SOCIALPRENEUR_SITE: SiteManifest = {
  tenant: 'socialpreneur',
  label: 'Asta Cafe Landing',
  // Dead for this tenant: the Official Store page and landing `packages` tiers
  // (the landing is bespoke).
  // NOTE: the Home tab + Featured Packages were kept because /storefront was the
  // "Where to Buy" destination. It no longer is — this runs as an information
  // site, nothing links to /storefront, and information-site-only mode unmounts
  // it. They are left visible on purpose, as the surfaces that would come back
  // first if the client ever switches selling on.
  adminHide: {
    storefrontTabs: ['official'],
    sidebarItems: ['/landing-packages'],
  },
  groups: [
    {
      id: 'landing',
      label: 'Landing page',
      sections: [
        {
          id: 'hero',
          label: 'Hero',
          fields: [
            {
              id: 'badge',
              kind: 'text',
              label: 'Top badge',
              default: 'Premium Wellness Coffee · With Inulin & Astaxanthin',
            },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Wellness in\nevery sip.',
              hint: 'Line breaks are kept.',
            },
            {
              id: 'titleAccent',
              kind: 'text',
              label: 'Title accent (gold line)',
              default: 'Powered by nature.',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Asta Cafe is a premium coffee blend enriched with natural ingredients that support your health, vitality, and daily wellness.',
            },
            {
              id: 'ctaPrimary',
              kind: 'text',
              label: 'Primary button',
              default: 'Explore the Blend',
            },
            {
              id: 'ctaSecondary',
              kind: 'text',
              label: 'Secondary button',
              default: 'The Science of Astaxanthin',
            },
            {
              id: 'chips',
              kind: 'stringList',
              label: 'Trust chips',
              default: ['Halal Certified', 'NAXA Verified', 'Non-GMO', 'No Artificial Flavors'],
              maxItems: 8,
            },
            {
              id: 'boxBadge',
              kind: 'text',
              label: 'Product box badge',
              default: '15 sachets × 21g',
            },
            {
              id: 'productImage',
              kind: 'image',
              label: 'Product photo',
              defaultUrl: '/tenants/socialpreneur/astabox.png',
            },
          ],
        },
        {
          id: 'blend',
          label: 'The Blend',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'The Asta Cafe Promise' },
            { id: 'script', kind: 'text', label: 'Script line', default: 'A Better Coffee.' },
            { id: 'title', kind: 'text', label: 'Title', default: 'A Better You.' },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              default:
                "More than a daily cup — Asta Cafe brings together nature's most trusted ingredients so every serving works for your body. Rich, creamy, and perfectly balanced, with wellness built into every sip.",
            },
            {
              id: 'pillars',
              kind: 'objectList',
              label: 'Promise cards',
              itemFields: [
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  title: 'Premium Quality',
                  desc: 'Carefully selected ingredients for your daily wellness.',
                },
                {
                  title: 'Delicious Taste',
                  desc: 'Rich, creamy and perfectly balanced for any time of the day.',
                },
                {
                  title: 'Health & Wellness',
                  desc: 'A smart choice for a balanced and healthy lifestyle.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'products',
          label: 'Products carousel',
          hint: 'Showcase-only (no prices/cart). The section is hidden on the site until at least one slide is added.',
          fields: [
            {
              id: 'display',
              kind: 'select',
              label: 'Display style',
              options: [
                { value: 'carousel', label: 'Big carousel (one slide at a time)' },
                { value: 'grid', label: 'Catalog grid (multi-column cards)' },
              ],
              default: 'carousel',
            },
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'Our Products' },
            { id: 'title', kind: 'text', label: 'Title', default: 'The Asta Cafe lineup' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default: 'A closer look at what we make.',
            },
            {
              id: 'slides',
              kind: 'objectList',
              label: 'Product slides',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Product image' },
                { id: 'tag', kind: 'text', label: 'Tag (small label)' },
                { id: 'title', kind: 'text', label: 'Caption' },
              ],
              default: [],
              maxItems: 12,
            },
          ],
        },
        {
          id: 'gallery',
          label: 'Gallery',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'The Science, Illustrated' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Wellness, explained' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Explore the research behind every ingredient in Asta Cafe — one card at a time.',
            },
            {
              id: 'slides',
              kind: 'objectList',
              label: 'Slides',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Image' },
                { id: 'tag', kind: 'text', label: 'Tag' },
                { id: 'title', kind: 'text', label: 'Title' },
              ],
              default: [
                {
                  imageKey: '/tenants/socialpreneur/gallery/asta-cafe.png',
                  tag: 'The Blend',
                  title: 'Asta Cafe — Wellness in Every Sip',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/astaxanthin-brain.png',
                  tag: 'Astaxanthin',
                  title: 'For Brain & Cognitive Function',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/astaxanthin-heart.png',
                  tag: 'Astaxanthin',
                  title: 'For Cardiovascular Health',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/astaxanthin-eye.png',
                  tag: 'Astaxanthin',
                  title: 'For Eye Health',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/astaxanthin-exercise.png',
                  tag: 'Astaxanthin',
                  title: 'For Exercise & Recovery',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/astaxanthin-inflammation.png',
                  tag: 'Astaxanthin',
                  title: 'For Anti-Inflammatory Effects',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/coffee.png',
                  tag: 'Coffee',
                  title: 'Health Benefits of Coffee',
                },
                {
                  imageKey: '/tenants/socialpreneur/gallery/raw-brown-sugar.png',
                  tag: 'Raw Brown Sugar',
                  title: 'Health Benefits of Raw Brown Sugar',
                },
              ],
              maxItems: 16,
            },
          ],
        },
        {
          id: 'ingredients',
          label: 'Ingredients',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'Powered by Nature' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Seven natural ingredients' },
            {
              id: 'lead',
              kind: 'textarea',
              label: 'Lead paragraph',
              default:
                'Each ingredient is chosen for a reason. Together they make a coffee that supports your wellness, sip after sip.',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Ingredient cards',
              itemFields: [
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'tag', kind: 'text', label: 'Tag' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  name: 'Astaxanthin',
                  tag: 'Antioxidant',
                  desc: 'A powerful natural antioxidant that helps protect cells from oxidative stress and supports overall wellness.',
                },
                {
                  name: 'Chicory Inulin',
                  tag: 'Prebiotic Fiber',
                  desc: 'A prebiotic fiber that supports gut health, digestion, and nutrient absorption.',
                },
                {
                  name: 'Digestive Enzyme',
                  tag: 'Papaya & Pineapple',
                  desc: 'A natural enzyme blend from papaya and pineapple that helps break down food, improve digestion, and enhance nutrient utilization.',
                },
                {
                  name: 'Moringa Oleifera',
                  tag: 'Superfood',
                  desc: 'A nutrient-rich superfood known for its vitamins, minerals, and natural anti-inflammatory properties.',
                },
                {
                  name: 'Coffee',
                  tag: 'Arabica & Robusta',
                  desc: 'Carefully selected coffee beans delivering rich aroma and smooth taste.',
                },
                {
                  name: 'Raw Brown Sugar',
                  tag: 'Natural Sweetener',
                  desc: 'Minimally processed to retain natural minerals and rich, caramel-like flavor.',
                },
                {
                  name: 'Non-Dairy Creamer',
                  tag: 'Dairy-Free',
                  desc: 'Creamy and smooth for the perfect balance in every cup, without the dairy.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'astaxanthin',
          label: 'Astaxanthin feature',
          fields: [
            { id: 'badge', kind: 'text', label: 'Badge', default: 'The Flagship Ingredient' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Astaxanthin —' },
            {
              id: 'titleAccent',
              kind: 'text',
              label: 'Title accent (gold line)',
              default: "nature's strongest antioxidant",
            },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              hint: '*text* renders as the highlighted species name.',
              default:
                'A powerful natural carotenoid derived from *Haematococcus pluvialis* microalgae. It crosses the blood–brain barrier to protect your cells from oxidative stress — supporting brain, heart, eyes, and whole-body wellness.',
            },
            {
              id: 'traits',
              kind: 'stringList',
              label: 'Trait chips',
              default: [
                'Crosses the blood–brain barrier',
                'Fat-soluble for absorption',
                'Non-GMO & safe',
                'From microalgae',
              ],
              maxItems: 8,
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'Explore the research' },
            {
              id: 'image',
              kind: 'image',
              label: 'Feature visual',
              defaultUrl: '/tenants/socialpreneur/astaxanthin.jpg',
            },
            { id: 'oracLabel', kind: 'text', label: 'ORAC card — label', default: 'ORAC Value' },
            { id: 'oracValue', kind: 'text', label: 'ORAC card — value', default: '2,822,200' },
            { id: 'oracUnit', kind: 'text', label: 'ORAC card — unit', default: 'µmol TE / 100g' },
            {
              id: 'statsHeading',
              kind: 'text',
              label: 'Stats heading',
              default: 'Stronger than you think',
            },
            {
              id: 'stats',
              kind: 'objectList',
              label: 'Comparison stats',
              itemFields: [
                { id: 'x', kind: 'text', label: 'Multiplier' },
                { id: 'label', kind: 'text', label: 'Label' },
              ],
              default: [
                { x: '6000×', label: 'more powerful than Vitamin C' },
                { x: '800×', label: 'stronger than CoQ10' },
                { x: '550×', label: 'stronger than Green Tea Polyphenols' },
                { x: '500×', label: 'stronger than Vitamin E' },
              ],
              maxItems: 8,
            },
            {
              id: 'statsNote',
              kind: 'text',
              label: 'Stats footnote',
              default: 'Based on ORAC (Oxygen Radical Absorbance Capacity)',
            },
            {
              id: 'benefitsHeading',
              kind: 'text',
              label: 'Benefits heading',
              default: 'Backed by human clinical studies',
            },
            {
              id: 'benefits',
              kind: 'objectList',
              label: 'Benefit tiles',
              itemFields: [
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  title: 'Brain & Cognitive',
                  desc: 'Supports memory, focus, mental clarity and long-term cognitive health.',
                },
                {
                  title: 'Cardiovascular',
                  desc: 'Supports healthy blood pressure, lipid profile and circulation.',
                },
                {
                  title: 'Eye Health',
                  desc: 'Reduces eye fatigue and supports healthy vision and retinal blood flow.',
                },
                {
                  title: 'Exercise & Recovery',
                  desc: 'Improves endurance and speeds up muscle recovery after training.',
                },
                {
                  title: 'Anti-Inflammatory',
                  desc: 'Helps calm inflammation and reduce oxidative stress throughout the body.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'how',
          label: 'How to Use',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'Simple Daily Ritual' },
            { id: 'title', kind: 'text', label: 'Title', default: 'How to enjoy Asta Cafe' },
            {
              id: 'steps',
              kind: 'objectList',
              label: 'Steps',
              itemFields: [
                { id: 'n', kind: 'text', label: 'Step #' },
                { id: 'text', kind: 'textarea', label: 'Instruction' },
              ],
              default: [
                { n: '1', text: 'Empty the contents of one (1) sachet (21g) into a cup.' },
                { n: '2', text: 'Add 150ml of hot water.' },
                { n: '3', text: 'Stir well and enjoy — any time of day.' },
              ],
              maxItems: 6,
            },
            { id: 'cautionTitle', kind: 'text', label: 'Caution — title', default: 'Caution' },
            {
              id: 'cautionBody',
              kind: 'textarea',
              label: 'Caution — body',
              default:
                'For adult use only. Not recommended for children, pregnant or lactating women. Store in a cool, dry place away from direct heat and sunlight.',
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Lifestyle photo',
              defaultUrl: '/tenants/socialpreneur/astacafe-cup.jpg',
            },
          ],
        },
        {
          id: 'certs',
          label: 'Certifications',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'Quality You Can Trust' },
            {
              id: 'title',
              kind: 'text',
              label: 'Title',
              default: 'Certified. Natural. Science-backed.',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Certification cards',
              itemFields: [
                { id: 'tag', kind: 'text', label: 'Seal tag' },
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'by', kind: 'text', label: 'Issued by' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  tag: 'HALAL',
                  title: 'Halal Certified',
                  by: "Islamic Da'wah Council of the Philippines (IDCP)",
                  desc: 'Assured quality you can trust — proudly halal-certified for your peace of mind.',
                },
                {
                  tag: 'NAXA',
                  title: 'NAXA Verified',
                  by: 'Natural Algae Astaxanthin Association',
                  desc: 'Verified natural algae astaxanthin — pure, potent and responsibly sourced.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'faq',
          label: 'FAQ',
          hint: 'Starter answers drafted from the copy already on this page — review and edit them as the client’s own words before launch.',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'Questions & Answers' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Frequently asked' },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Questions',
              itemFields: [
                { id: 'q', kind: 'text', label: 'Question' },
                { id: 'a', kind: 'textarea', label: 'Answer' },
              ],
              default: [
                {
                  q: 'What is Asta Cafe?',
                  a: 'A premium coffee blend enriched with natural ingredients that support your health, vitality, and daily wellness — enriched with inulin and astaxanthin.',
                },
                {
                  q: "What's in every sachet?",
                  a: 'Seven natural ingredients: astaxanthin, chicory inulin, a papaya & pineapple digestive enzyme blend, moringa oleifera, coffee, raw brown sugar, and a non-dairy creamer.',
                },
                {
                  q: 'How do I prepare it?',
                  a: 'Empty one 21g sachet into a cup, add 150ml of hot water, stir well and enjoy — any time of day.',
                },
                {
                  q: 'How many sachets come in a box?',
                  a: 'Each box contains 15 sachets of 21g.',
                },
                {
                  q: 'Is Asta Cafe halal certified?',
                  a: "Yes — Asta Cafe is halal-certified by the Islamic Da'wah Council of the Philippines (IDCP), and its astaxanthin is verified by the Natural Algae Astaxanthin Association (NAXA).",
                },
                {
                  q: 'Where does the astaxanthin come from?',
                  a: 'It is a natural carotenoid derived from Haematococcus pluvialis microalgae — non-GMO, and fat-soluble for absorption.',
                },
                {
                  q: 'Who should not drink Asta Cafe?',
                  a: 'It is for adult use only. It is not recommended for children, or for pregnant or lactating women.',
                },
                {
                  q: 'Where can I buy Asta Cafe?',
                  a: 'Get in touch and our team will point you to a SocialPreneur distributor near you.',
                },
              ],
              maxItems: 20,
            },
          ],
        },
        {
          id: 'cta',
          label: 'Call to action',
          fields: [
            { id: 'script', kind: 'text', label: 'Script line', default: 'A Better Coffee.' },
            { id: 'title', kind: 'text', label: 'Title', default: 'A Better You.' },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              default:
                'Ready to make wellness part of your daily ritual? Order from our official online stores, or reach out to a SocialPreneur distributor near you.',
            },
            {
              id: 'ctaPrimary',
              kind: 'text',
              label: 'Header pill button',
              default: 'Where to Buy',
              hint: 'The gold button in the site header. It scrolls down to this Contact section.',
            },
            {
              id: 'ctaSecondary',
              kind: 'text',
              label: 'Secondary button',
              default: 'Get in Touch',
            },
            {
              id: 'contactEmail',
              kind: 'text',
              label: 'Contact email',
              default: 'info@socialpreneurinc.com',
            },
            {
              id: 'shopsTitle',
              kind: 'text',
              label: 'Online stores heading',
              default: 'Buy online',
              hint: 'Sits above the marketplace buttons. Clear the store list below to hide the whole block.',
            },
            {
              id: 'shops',
              kind: 'objectList',
              label: 'Online store links',
              maxItems: 6,
              itemFields: [
                { id: 'label', kind: 'text', label: 'Store name' },
                { id: 'url', kind: 'text', label: 'Store link (https://…)' },
                { id: 'imageKey', kind: 'image', label: 'Store logo' },
              ],
              // A row with no URL is skipped, so an unfinished row can't render a
              // dead button — same rule as the contact email above.
              default: [
                {
                  label: 'Shopee',
                  url: 'https://ph.shp.ee/XoZqDS3Q',
                  imageKey: '/tenants/socialpreneur/icons/shopee.png',
                },
                {
                  label: 'Lazada',
                  url: 'https://s.lazada.com.ph/s.Z6BNO9?c=s&t=p-i4pB7KA-sRBy7gM',
                  imageKey: '/tenants/socialpreneur/icons/lazada.png',
                },
                {
                  label: 'TikTok Shop',
                  url: 'https://vt.tiktok.com/ZS4ux48Fo/?page=TikTokShop',
                  imageKey: '/tenants/socialpreneur/icons/tiktok.svg',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'about',
      label: 'About page',
      sections: [
        {
          id: 'hero',
          label: 'Page header',
          hint: 'DRAFT COPY. Nothing on this page came from the client — the design handoff had no About/Mission screen. Replace it with SocialPreneur Inc.’s own words before launch.',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Eyebrow', default: 'About SocialPreneur Inc.' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Empowering Communities' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default: 'The company behind Asta Cafe.',
            },
          ],
        },
        {
          id: 'story',
          label: 'About SocialPreneur',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Who we are' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Paragraphs',
              hint: 'One row per paragraph. **bold**, *italic*, and rows starting with "- " become bullets.',
              default: [
                'SocialPreneur Inc. is the company behind Asta Cafe — a premium coffee blend enriched with inulin and astaxanthin.',
                'We bring trusted natural ingredients together into something simple enough to enjoy every morning, and considered enough to be worth drinking.',
              ],
            },
          ],
        },
        {
          id: 'mission',
          label: 'Our Mission',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Our mission' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Paragraphs',
              default: [
                'Empowering Communities is the idea our work is built around: wellness people can afford to make part of an ordinary day, and livelihood for the people who bring it to them.',
              ],
            },
            {
              id: 'pillars',
              kind: 'objectList',
              label: 'Mission cards',
              itemFields: [
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  title: 'Wellness for every day',
                  desc: 'Products made to be used daily, not saved for special occasions.',
                },
                {
                  title: 'Quality we can show',
                  desc: 'Certified and verified ingredients, with the paperwork to back it up.',
                },
                {
                  title: 'Communities first',
                  desc: 'Growth shared with the distributors and communities who carry our products.',
                },
              ],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'cta',
          label: 'Closing call to action',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Talk to us' },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              default:
                'Questions about Asta Cafe, or about carrying it in your area? We would be glad to hear from you.',
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'Get in Touch' },
          ],
        },
      ],
    },
  ],
};
